import {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  useMemo,
} from "react";
import "../../../styles/AuditBrowser.css";
import { useFetchFunction } from "../../../utils/hooks/UseFetchUrl";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SelectContext from "../../../utils/Context/SelectContext";
import { Menu, message, Dropdown } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import ScrollDown from "../../../components/ScrollDown";
import dayjs from "dayjs";
import { base_url } from "../../../utils/apiConfig";
import { debounce } from "lodash";
import CustomDropdown from "../../../components/common/CustomDropdown";
import CustomSearchBar from "../../../components/common/CustomSearchBar";
import CustomDataRangeSelect from "../../../components/common/CustomDataRangeSelect";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import { getScoreConfig } from "../../../utils/hooks/useScoreScale1";
import AIInsightModal from "../../../components/modals/AIInsightModal";
import Pagination from "./Pagination";

const AuditBrowser = () => {
  const context = useContext(SelectContext);
  const location = useLocation();
  const navState = location.state;
  const navigate = useNavigate();

  const {
    selectedOption,
    setSelectedOption,
    selectedOption1,
    setSelectedOption1,
    setSelectedQueId,
    flag,
    setFlag,
  } = context;

  // ==================================== States ====================================
  const {
    loading: auditStoreLoading,
    data: auditStoreData,
    getData: getAuditStoreData,
  } = useFetchFunction();

  const {
    loading: filtersLoading,
    data: filtersData,
    getData: getFiltersData,
  } = useFetchFunction();

  const [cycleId, setCycleId] = useState(null);
  const [detailsData, setDetailsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAIModal, setShowAIModal] = useState(false);
  const [selectedAIStore, setSelectedAIStore] = useState(null);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState({
    searchText: "",
    selectedState: "",
    selectedCity: "",
    selectedStartDate: null,
    selectedEndDate: null,
    selectedAuditType: "",
    selectedQuesType: "",
    selectedOption1: "",
    selectedStore: null,
    selectedScoreBand: "",
  });
  const [filterOptions, setFilterOptions] = useState({
    questionnaire_types: [],
    audit_cycles: [],
    audit_types: [],
    cities: [],
    states: [],
    stores: [],
    scores: [],
  });

  const {
    searchText,
    selectedState,
    selectedCity,
    selectedStartDate,
    selectedEndDate,
    selectedAuditType,
    selectedQuesType,
    selectedStore,
    selectedScoreBand,
  } = filters;

  const scoreOptions = [...filterOptions.scores]
    .sort((a, b) => {
      const order = ["excellent", "good", "average", "poor"];
      return order.indexOf(a.value) - order.indexOf(b.value);
    })
    .map((score) => ({
      ...score,
      displayLabel: `${score.value.charAt(0).toUpperCase() + score.value.slice(1)} : ${score.label}`,
    }));

  const getScoreCategory = (percentage) => {
    if (percentage == null) return null;

    const score = Number(percentage);

    const scoreOption = filterOptions.scores.find((item) => {
      const [min, max] = item.label
        .split("to")
        .map((value) => Number(value.trim()));

      return score >= min && score <= max;
    });

    return scoreOption?.value || null;
  };

  const updateFilter = (name, value) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }));
  };

  const setSearchText = (value) => updateFilter("searchText", value);
  const setSelectedState = (value) => updateFilter("selectedState", value);
  const setSelectedCity = (value) => updateFilter("selectedCity", value);
  const setSelectedStartDate = (value) =>
    updateFilter("selectedStartDate", value);
  const setSelectedEndDate = (value) => updateFilter("selectedEndDate", value);
  const setSelectedAuditType = (value) =>
    updateFilter("selectedAuditType", value);
  const setSelectedStore = (value) => updateFilter("selectedStore", value);
  const setSelectedScoreBand = (value) =>
    updateFilter("selectedScoreBand", value);

  // ==================================== Derived Data ====================================
  const queId = filterOptions.questionnaire_types?.[0]?.value;

  const pageStores = Array.isArray(auditStoreData)
    ? auditStoreData
    : auditStoreData?.audit_stores || [];

  const totalCount = Array.isArray(auditStoreData)
    ? auditStoreData.length
    : (auditStoreData?.total_count ?? 0);

  const totalPages = Array.isArray(auditStoreData)
    ? 1
    : Math.max(1, auditStoreData?.total_pages ?? 1);

  const visibleStores = pageStores;

  const auditCycleIdName = selectedOption1 || detailsData?.[0]?.value || "";
  const auditCycle = detailsData?.find(
    (cycle) => cycle.value === Number(auditCycleIdName),
  );

  const cycleName = auditCycle?.label
    ? auditCycle.label.replace(/\s+/g, "_")
    : "Unknown_Cycle";

  // ==================================== Query Builder / Fetch Refs ====================================
  const buildQueryParams = useCallback(
    (searchValue = "") => {
      const params = new URLSearchParams();

      if (searchValue.trim()) params.set("search", searchValue.trim());
      if (selectedQuesType) params.set("questionnaire_type", selectedQuesType);
      if (cycleId) params.set("audit_cycle", cycleId);
      if (selectedAuditType) params.set("audit_type", selectedAuditType);
      if (selectedCity) params.set("city_id", selectedCity);
      if (selectedState) params.set("state", selectedState);
      if (selectedStore) params.set("store_id", selectedStore);
      if (selectedStartDate) {
        params.set("start_date", dayjs(selectedStartDate).format("YYYY-MM-DD"));
      }
      if (selectedEndDate) {
        params.set("end_date", dayjs(selectedEndDate).format("YYYY-MM-DD"));
      }
      if (selectedScoreBand) params.set("score", selectedScoreBand);

      params.set("page", currentPage);
      params.set("page_count", pageSize);

      return params.toString();
    },
    [
      selectedQuesType,
      cycleId,
      selectedAuditType,
      selectedCity,
      selectedState,
      selectedStore,
      selectedStartDate,
      selectedEndDate,
      selectedScoreBand,
      currentPage,
      pageSize,
    ],
  );

  const getAuditStoreDataRef = useRef(getAuditStoreData);
  const getFiltersDataRef = useRef(getFiltersData);
  getAuditStoreDataRef.current = getAuditStoreData;
  getFiltersDataRef.current = getFiltersData;

  const runFetch = useCallback((query) => {
    const queryString = query ? `?${query}` : "";
    getAuditStoreDataRef.current(`/report/audit_store${queryString}`);
    const filterParams = new URLSearchParams(query);
    filterParams.delete("page");
    filterParams.delete("page_count");
    const filterQuery = filterParams.toString();
    getFiltersDataRef.current(
      `/report/audit_store_filters${filterQuery ? `?${filterQuery}` : ""}`,
    );
  }, []);

  const debouncedSearchFetch = useMemo(
    () => debounce((query) => runFetch(query), 400),
    [runFetch],
  );

  const buildQueryParamsRef = useRef(buildQueryParams);
  const searchTextRef = useRef(searchText);
  const isInitialSearchRender = useRef(true);
  searchTextRef.current = searchText;

  // ==================================== Effects ====================================
  useEffect(() => {
    buildQueryParamsRef.current = buildQueryParams;
  }, [buildQueryParams]);

  useEffect(() => {
    if (navState && cycleId != null) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [cycleId]);

  useEffect(() => {
    if (!navState?.questionnaireTypeId) return;
    setSelectedQueId(navState.questionnaireTypeId);
    setSelectedOption(navState.questionnaireTypeId);
  }, [navState]);

  useEffect(() => {
    const filteredCycles = Array.isArray(filterOptions?.audit_cycles)
      ? filterOptions.audit_cycles
      : [];

    setDetailsData(filteredCycles);

    if (navState?.auditCycleId != null) {
      const navCycleId = Number(navState.auditCycleId);

      const navCycleIsValid = filteredCycles.some(
        (cycle) => Number(cycle.value) === navCycleId,
      );

      if (navCycleIsValid) {
        setCycleId(navCycleId);
        setSelectedOption1(navCycleId);
      }
    }
  }, [filterOptions?.audit_cycles, navState, setSelectedOption1]);

  useEffect(() => {
    if (filtersData) {
      setFilterOptions({
        questionnaire_types: filtersData.questionnaire_types || [],
        audit_cycles: filtersData.audit_cycles || [],
        audit_types: filtersData.audit_types || [],
        cities: filtersData.cities || [],
        states: filtersData.states || [],
        stores: filtersData.stores || [],
        scores: filtersData.scores || [],
      });
    }
  }, [filtersData]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    if (queId && flag) {
      setSelectedQueId(queId);
      setFlag(false);
    }
  }, [queId, flag]);

  // Reset to page 1 whenever any filter (other than page/pageSize itself) changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchText,
    selectedState,
    selectedCity,
    selectedQuesType,
    selectedOption1,
    selectedAuditType,
    selectedStore,
    selectedStartDate,
    selectedEndDate,
    selectedScoreBand,
    cycleId,
  ]);

  useEffect(() => {
    runFetch(buildQueryParams(searchTextRef.current));
  }, [buildQueryParams, runFetch]);

  useEffect(() => {
    if (isInitialSearchRender.current) {
      isInitialSearchRender.current = false;
      return undefined;
    }

    debouncedSearchFetch(buildQueryParamsRef.current(searchText));

    return () => debouncedSearchFetch.cancel();
  }, [searchText, debouncedSearchFetch]);

  // ==================================== Functions ====================================
  const handleCycleChange = (value) => {
    setSelectedOption1(value);
    setCycleId(value ? Number(value) : null);
  };

  const rememberAuditReportOrigin = (auditStoreId) => {
    sessionStorage.setItem(
      `audit-report-origin-${auditStoreId}`,
      window.location.pathname + window.location.search,
    );
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      searchText: "",
      selectedState: "",
      selectedCity: "",
      selectedStartDate: null,
      selectedEndDate: null,
      selectedAuditType: "",
      selectedQuesType: "",
      selectedOption1: "",
      selectedStore: null,
      selectedScoreBand: "",
    };

    setFilters(clearedFilters);
    setCycleId(null);
    setSelectedOption1("");
    setCurrentPage(1);
  };

  const buildExcelQueryParams = () => {
    const params = new URLSearchParams();

    if (searchText.trim()) params.set("search", searchText.trim());
    if (selectedQuesType) params.set("questionnaire_type", selectedQuesType);
    if (cycleId) params.set("audit_cycle", cycleId);
    if (selectedAuditType) params.set("audit_type", selectedAuditType);
    if (selectedCity) params.set("city_id", selectedCity);
    if (selectedState) params.set("state", selectedState);
    if (selectedStore) params.set("store_id", selectedStore);
    if (selectedScoreBand) params.set("score", selectedScoreBand);
    if (selectedStartDate) {
      params.set("start_date", dayjs(selectedStartDate).format("YYYY-MM-DD"));
    }
    if (selectedEndDate) {
      params.set("end_date", dayjs(selectedEndDate).format("YYYY-MM-DD"));
    }

    // Intentionally NO page / page_count
    return params.toString();
  };

  const downloadExcelReport = async (url, errorLabel) => {
    try {
      const token = localStorage.getItem("authToken");
      const userDetails = localStorage.getItem("userDetails");
      const clientName = userDetails
        ? JSON.parse(userDetails)?.client?.name
        : null;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type":
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          Authorization: `token ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Error fetching Excel: ${response.statusText}`);
      }
      const blob = await response.blob();
      const url_ = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url_;
      a.download = clientName
        ? `${clientName}_all_audits.xlsx`
        : "all_audits.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.log(errorLabel, error);
    }
  };

  const exportInExcel = async () => {
    const base = `${base_url.protocol}://${base_url.hostname}:${base_url.port}${base_url.api_base_path}client/report_browser_filtered_xlsx_report?`;

    const queryParams = buildExcelQueryParams();
    const url = base + queryParams;

    await downloadExcelReport(
      url,
      "Something went wrong downloading Summary Excel file",
    );
  };

  const menu = (
    <Menu
      items={[
        {
          key: "summary",
          label: (
            <>
              Export in Excel <br />
              <span style={{ fontSize: "14px", color: "#888" }}>
                Download all selcted Audit Browser in Excel.
              </span>
            </>
          ),
          onClick: () => {
            message.loading(
              "Downloading Audit Browser in Excel File, please wait...",
              2,
            );
            exportInExcel();
          },
        },
      ]}
    />
  );

  // ==================================== Render ====================================
  return (
    <div className="rbCard">
      <div className="rbHeaderBlock">
        <p className="rbTitle">Audit Browser</p>
        <p className="rbSubtitle">
          {totalCount} completed {totalCount > 1 ? "Audits" : "Audit"}
        </p>
      </div>

      <div className="filter-container">
        <div className="rbFilterRow first-filter">
          <CustomSearchBar
            value={searchText}
            onChange={setSearchText}
            placeholder="Search by Store Code or Store Name"
            className="searchbar"
          />

          <div className="filter-row-actions">
            <Dropdown overlay={menu} trigger={["click", "hover"]}>
              <div
                className="rbFilterBtn"
                style={{
                  background: "#fff",
                  color: "var(--rb-blue)",
                  border: "1px solid var(--rb-blue)",
                }}
              >
                <ExportOutlined
                  style={{
                    fontSize: "14px",
                    marginRight: "8px",
                    fontWeight: "bold",
                  }}
                />
                Export
              </div>
            </Dropdown>
          </div>
        </div>

        <div className="rbFilterRow">
          <CustomDropdown
            label="Audit Cycle"
            value={selectedOption1}
            options={filterOptions.audit_cycles}
            onChange={handleCycleChange}
            optionValue="value"
            optionLabel="label"
            placeholder="All"
            disabled={filtersLoading}
          />
          <CustomDropdown
            label="Audit Type"
            value={selectedAuditType}
            options={filterOptions.audit_types}
            onChange={setSelectedAuditType}
            optionValue="value"
            optionLabel="label"
            placeholder="All"
            disabled={filtersLoading}
          />
          <CustomDropdown
            label="Store"
            value={selectedStore}
            options={filterOptions.stores}
            onChange={setSelectedStore}
            optionValue="value"
            optionLabel="label"
            placeholder="All"
            showSearch={true}
            disabled={filtersLoading}
          />
          <CustomDataRangeSelect
            selectedStartDate={selectedStartDate}
            setSelectedStartDate={setSelectedStartDate}
            selectedEndDate={selectedEndDate}
            setSelectedEndDate={setSelectedEndDate}
          />
          <CustomDropdown
            label="State"
            value={selectedState}
            options={filterOptions.states}
            onChange={(value) => {
              setSelectedState(value);
              setSelectedCity("");
            }}
            optionValue="value"
            optionLabel="label"
            placeholder="All States"
            disabled={filtersLoading}
          />
          <CustomDropdown
            label="City"
            value={selectedCity}
            options={filterOptions.cities}
            onChange={setSelectedCity}
            optionValue="value"
            optionLabel="label"
            placeholder="All Cities"
            disabled={filtersLoading}
          />
          <CustomDropdown
            label="Score"
            value={selectedScoreBand}
            options={scoreOptions}
            onChange={setSelectedScoreBand}
            optionValue="value"
            optionLabel="displayLabel"
            placeholder="All Scores"
            disabled={filtersLoading}
          />
          <button
            type="button"
            className="rbClearAll mt-4"
            onClick={handleClearFilters}
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="rbTableWrap">
        <table className="rbTable">
          <thead>
            <tr>
              <th style={{ maxWidth: 90 }}>Store Code</th>
              <th style={{ minWidth: 200, maxWidth: 250 }}>Store</th>
              <th style={{ minWidth: 200, maxWidth: 250 }}>Audit Cycle</th>
              <th className="th-left">City</th>
              <th className="th-left">Audit Type</th>
              <th className="th-left">Audit Date</th>
              <th className="th-center">Score</th>
              <th className="th-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {auditStoreLoading ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: "center", padding: "4rem" }}
                >
                  <LoadingSpinner />
                </td>
              </tr>
            ) : visibleStores.length > 0 ? (
              visibleStores.map((row, rowIndex) => {
                const percentage = row.total_score?.percentage;
                const scoreCategory = getScoreCategory(percentage);

                const scoreConfig = getScoreConfig(percentage, {
                  [scoreCategory]: filterOptions.scores.find(
                    (score) => score.value === scoreCategory,
                  )?.label,
                });
                return (
                  <tr
                    key={rowIndex}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      rememberAuditReportOrigin(row.audit_store_id);
                      window.open(
                        `/auditReport/${row.audit_store_id}`,
                        "_blank",
                      );
                    }}
                  >
                    <td>{row.store_code ?? "N/A"}</td>
                    <td>{row.store_name ?? "N/A"}</td>
                    <td>{row.audit_cycle ?? "N/A"}</td>
                    <td>{row.city_name ?? "N/A"}</td>
                    <td>
                      {row.audit_type
                        ? row.audit_type.charAt(0).toUpperCase() +
                          row.audit_type.slice(1).toLowerCase()
                        : "N/A"}
                    </td>
                    <td>
                      {row.audit_date
                        ? dayjs(row.audit_date).format("Do MMM YYYY")
                        : "---"}
                    </td>
                    <td style={{ textAlign: "center", padding: 0 }}>
                      <span
                        className="rbScoreBadge"
                        style={{
                          backgroundColor: scoreConfig.bgColor,
                          color: scoreConfig.color,
                        }}
                      >
                        {percentage != null ? `${percentage}%` : "N/A"}
                      </span>
                    </td>
                    <td
                      onClick={(e) => e.stopPropagation()}
                      style={{ textAlign: "center" }}
                    >
                      <div className="rbActionButtons">
                        {/* <Tooltip title="AI Insights">
                          <button
                            type="button"
                            className="rbAiBtn"
                            onClick={() => {
                              setSelectedAIStore(row);
                              setShowAIModal(true);
                            }}
                          >
                            <span className="geminiSparkle">✦</span>
                          </button>
                        </Tooltip> */}
                        <Link
                          to={{
                            pathname: `/auditReport/${row.audit_store_id}`,
                            state: {
                              selectedOption,
                              fromUrl:
                                window.location.pathname +
                                window.location.search,
                            },
                          }}
                          onClick={() =>
                            rememberAuditReportOrigin(row.audit_store_id)
                          }
                          target="_blank"
                        >
                          <button className="rbViewBtn">VIEW REPORT</button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    textAlign: "center",
                    padding: "3rem",
                    color: "#6b7280",
                  }}
                >
                  No audits found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalCount={totalCount}
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
      />
      <ScrollDown />

      {showAIModal && selectedAIStore && (
        <AIInsightModal
          open={showAIModal}
          onClose={() => {
            setShowAIModal(false);
            setSelectedAIStore(null);
          }}
          auditStoreId={selectedAIStore.audit_store_id}
          overallScore={selectedAIStore.total_score?.percentage ?? 0}
        />
      )}
    </div>
  );
};

export default AuditBrowser;
